import { useState } from 'react';
import { Link } from 'react-router-dom';
import CreateFolderModal from './CreateFolderModal';
import type { Folder } from '@/types';
import styles from './FolderList.module.css';

interface FolderListProps {
  folders: Folder[];
  isLoading: boolean;
  activeFolderId?: string;
  onNavigate?: () => void;
}

export default function FolderList({
  folders,
  isLoading,
  activeFolderId,
  onNavigate,
}: FolderListProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const systemFolders = folders.filter((f) => f.is_system);
  const userFolders = folders.filter((f) => !f.is_system);
  const sharedFolders = userFolders.filter((f) => f.is_shared && !f.is_owner);
  const ownFolders = userFolders.filter((f) => !f.is_shared || f.is_owner);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={`${styles.skeleton} skeleton`} />
        <div className={`${styles.skeleton} skeleton`} />
        <div className={`${styles.skeleton} skeleton`} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* System Folders */}
      {systemFolders.length > 0 && (
        <div className={styles.section}>
          {systemFolders.map((folder) => (
            <Link
              key={folder.id}
              to={`/app/folder/${folder.id}`}
              className={`${styles.folderLink} ${activeFolderId === folder.id ? styles.active : ''}`}
              onClick={onNavigate}
            >
              <span className={styles.icon}>
                {folder.system_type === 'favorites' ? '★' : '↻'}
              </span>
              <span className={styles.name}>{folder.name}</span>
              <span className={styles.count}>{folder.recipe_count}</span>
            </Link>
          ))}
        </div>
      )}

      {/* User Folders */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>My Folders</span>
          <button
            className={styles.addButton}
            onClick={() => setShowCreateModal(true)}
            aria-label="Create folder"
          >
            +
          </button>
        </div>
        {ownFolders.length === 0 ? (
          <p className={styles.empty}>No folders yet</p>
        ) : (
          ownFolders.map((folder) => (
            <Link
              key={folder.id}
              to={`/app/folder/${folder.id}`}
              className={`${styles.folderLink} ${activeFolderId === folder.id ? styles.active : ''}`}
              onClick={onNavigate}
            >
              <span className={styles.icon}>📁</span>
              <span className={styles.name}>{folder.name}</span>
              <span className={styles.count}>{folder.recipe_count}</span>
            </Link>
          ))
        )}
      </div>

      {/* Shared Folders */}
      {sharedFolders.length > 0 && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Shared with Me</span>
          {sharedFolders.map((folder) => (
            <Link
              key={folder.id}
              to={`/app/folder/${folder.id}`}
              className={`${styles.folderLink} ${activeFolderId === folder.id ? styles.active : ''}`}
              onClick={onNavigate}
            >
              <span className={styles.icon}>👥</span>
              <span className={styles.name}>{folder.name}</span>
              <span className={styles.owner}>by {folder.owner_username}</span>
            </Link>
          ))}
        </div>
      )}

      <CreateFolderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
}
